import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check, Save, CreditCard, ExternalLink, Play, Square, Trash2, AlertTriangle } from "lucide-react";


const experienceLevels = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "elite", label: "Elite" },
];

const trainingFrequencies = [
  { value: "1-2", label: "1–2 days/week" },
  { value: "3-4", label: "3–4 days/week" },
  { value: "5-6", label: "5–6 days/week" },
  { value: "daily", label: "Every day" },
];

const mentalChallengeOptions = [
  "Pre-game anxiety", "Focus under pressure", "Confidence", "Recovery from setbacks",
  "Consistency", "Motivation", "Fear of failure", "Overthinking",
];

const goalOptions = [
  "Perform better in competition", "Build confidence", "Manage pressure",
  "Recover faster mentally", "Improve focus", "Develop consistency",
  "Stay motivated", "Handle setbacks",
];

const ProfilePage = () => {
  const { user, subscription } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast.error("Could not open subscription management. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sports, setSports] = useState<any[]>([]);
  const [voices, setVoices] = useState<any[]>([]);

  const [fullName, setFullName] = useState("");
  const [sportId, setSportId] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("");
  const [trainingFrequency, setTrainingFrequency] = useState("");
  const [mentalChallenges, setMentalChallenges] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const [preferredVoiceId, setPreferredVoiceId] = useState("");
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setPlayingVoiceId(null);
  }, []);

  const playPreview = useCallback(async (voiceId: string, previewUrl: string | null) => {
    stopPreview();
    if (!previewUrl) {
      toast.error("No preview available for this voice");
      return;
    }
    const audio = new Audio(previewUrl);
    audio.onended = () => setPlayingVoiceId(null);
    audioRef.current = audio;
    setPlayingVoiceId(voiceId);
    try {
      await audio.play();
    } catch {
      setPlayingVoiceId(null);
      toast.error("Failed to play preview");
    }
  }, [stopPreview]);

  useEffect(() => {
    return () => stopPreview();
  }, [stopPreview]);

  useEffect(() => {
    const load = async () => {
      if (!user) return;

      const [{ data: profile }, { data: sportsData }, { data: voicesData }] = await Promise.all([
        supabase.from("profiles").select("*").eq("user_id", user.id).single(),
        supabase.from("sports").select("*").eq("is_active", true).order("name"),
        supabase.from("voice_options").select("*").eq("is_active", true).order("sort_order"),
      ]);

      if (profile) {
        setFullName(profile.full_name || "");
        setSportId(profile.sport_id || "");
        setExperienceLevel(profile.experience_level || "");
        setTrainingFrequency(profile.training_frequency || "");
        setMentalChallenges(profile.mental_challenges || []);
        setGoals(profile.goals || []);
        setPreferredVoiceId(profile.preferred_voice_id || "");
      }
      setSports(sportsData || []);
      setVoices(voicesData || []);
      setLoading(false);
    };
    load();
  }, [user]);

  const toggleItem = (list: string[], setList: (v: string[]) => void, item: string) => {
    setList(list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          sport_id: sportId || null,
          experience_level: experienceLevel,
          training_frequency: trainingFrequency,
          mental_challenges: mentalChallenges,
          goals,
          preferred_voice_id: preferredVoiceId || null,
        })
        .eq("user_id", user.id);

      if (error) throw error;
      toast.success("Profile updated!");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold mb-2">Your Profile</h1>
          <p className="text-muted-foreground">Customize your athlete profile and preferences.</p>
        </div>

        <div className="space-y-8">
          {/* Name */}
          <Section title="Full Name">
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full h-12 rounded-xl border border-border/50 bg-secondary/30 px-4 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </Section>

          {/* Sport */}
          <Section title="Sport">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {sports.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSportId(s.id)}
                  className={`p-3 rounded-xl border text-sm transition-all ${
                    sportId === s.id
                      ? "border-primary bg-primary/10 text-foreground font-medium"
                      : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </Section>

          {/* Experience */}
          <Section title="Experience Level">
            <div className="grid grid-cols-2 gap-2">
              {experienceLevels.map((l) => (
                <button
                  key={l.value}
                  onClick={() => setExperienceLevel(l.value)}
                  className={`p-3 rounded-xl border text-sm transition-all ${
                    experienceLevel === l.value
                      ? "border-primary bg-primary/10 text-foreground font-medium"
                      : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Training Frequency */}
          <Section title="Training Frequency">
            <div className="grid grid-cols-2 gap-2">
              {trainingFrequencies.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setTrainingFrequency(f.value)}
                  className={`p-3 rounded-xl border text-sm transition-all ${
                    trainingFrequency === f.value
                      ? "border-primary bg-primary/10 text-foreground font-medium"
                      : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Mental Challenges */}
          <Section title="Mental Challenges">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {mentalChallengeOptions.map((c) => (
                <button
                  key={c}
                  onClick={() => toggleItem(mentalChallenges, setMentalChallenges, c)}
                  className={`p-3 rounded-xl border text-sm text-left transition-all flex items-center gap-2 ${
                    mentalChallenges.includes(c)
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                      mentalChallenges.includes(c) ? "bg-primary" : "border border-border"
                    }`}
                  >
                    {mentalChallenges.includes(c) && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  {c}
                </button>
              ))}
            </div>
          </Section>

          {/* Goals */}
          <Section title="Goals">
            <div className="grid grid-cols-1 gap-2">
              {goalOptions.map((g) => (
                <button
                  key={g}
                  onClick={() => toggleItem(goals, setGoals, g)}
                  className={`p-3 rounded-xl border text-sm text-left transition-all flex items-center gap-2 ${
                    goals.includes(g)
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border/50 bg-secondary/30 text-muted-foreground hover:border-primary/30"
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                      goals.includes(g) ? "bg-primary" : "border border-border"
                    }`}
                  >
                    {goals.includes(g) && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                  {g}
                </button>
              ))}
            </div>
          </Section>

          {/* Preferred Voice */}
          <Section title="Preferred Voice">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {voices.map((v) => (
                <div
                  key={v.id}
                  className={`p-4 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                    preferredVoiceId === v.id
                      ? "border-primary bg-primary/10"
                      : "border-border/50 bg-secondary/30 hover:border-primary/30"
                  }`}
                  onClick={() => setPreferredVoiceId(v.id)}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      playingVoiceId === v.id ? stopPreview() : playPreview(v.id, v.preview_audio_url);
                    }}
                    className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors"
                    aria-label={playingVoiceId === v.id ? "Stop preview" : "Play preview"}
                  >
                    {playingVoiceId === v.id ? (
                      <Square className="w-3 h-3 text-primary fill-primary" />
                    ) : (
                      <Play className="w-3 h-3 text-primary fill-primary ml-0.5" />
                    )}
                  </button>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{v.name}</div>
                    {v.description && (
                      <div className="text-xs text-muted-foreground mt-1">{v.description}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Save */}
          <Button variant="hero" size="lg" className="w-full" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Profile
          </Button>

          {/* Subscription Management */}
          <Section title="Subscription">
            {subscription.loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : subscription.subscribed ? (
              <div className="space-y-3">
                <div className="gradient-card rounded-xl border border-border/50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground capitalize">
                        {subscription.tier || "Active"} Plan
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {subscription.status === "trialing" ? "Free trial" : "Active subscription"}
                        {subscription.subscriptionEnd && ` · Renews ${new Date(subscription.subscriptionEnd).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                      {subscription.status === "trialing" ? "Trial" : "Active"}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                >
                  {portalLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Manage Subscription
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              </div>
            ) : (
              <div className="gradient-card rounded-xl border border-border/50 p-4 text-center">
                <p className="text-sm text-muted-foreground mb-3">No active subscription</p>
                <Button variant="hero" size="sm" onClick={() => window.location.href = "/#pricing"}>
                  View Plans
                </Button>
              </div>
            )}
          </Section>

          {/* Email display */}
          <div className="gradient-card rounded-xl border border-border/50 p-4 text-center">
            <p className="text-sm text-muted-foreground">
              Signed in as <span className="text-foreground">{user?.email}</span>
            </p>
          </div>

          {/* Delete Account */}
          <DeleteAccountSection />
        </div>
      </div>
    </AppLayout>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h2 className="font-display text-lg font-semibold">{title}</h2>
    {children}
  </div>
);

const DeleteAccountSection = () => {
  const { signOut } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== "DELETE") return;
    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      toast.success("Account deleted successfully.");
      await signOut();
    } catch {
      toast.error("Failed to delete account. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Section title="Danger Zone">
      {!showConfirm ? (
        <Button
          variant="outline"
          className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
          onClick={() => setShowConfirm(true)}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Delete Account
        </Button>
      ) : (
        <div className="rounded-xl border border-destructive/50 bg-destructive/5 p-4 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground text-sm">This action is permanent</p>
              <p className="text-sm text-muted-foreground mt-1">
                All your visualizations, sessions, progress data, and profile will be permanently deleted. This cannot be undone.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">
              Type <span className="font-mono font-bold text-foreground">DELETE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full h-10 rounded-lg border border-border/50 bg-secondary/30 px-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-destructive/50"
              placeholder="DELETE"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { setShowConfirm(false); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              disabled={confirmText !== "DELETE" || deleting}
              onClick={handleDelete}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Forever
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
};

export default ProfilePage;
